import { LoyaltyProgram } from "@/interfaces/loyaltyPrograms";
import axios from "@/lib/axios";
import { useMutation, useQuery } from "@tanstack/vue-query";

interface Props {
  immediate?: boolean;
}

interface Params {
  service?: number;
}

interface UpdateParams {
  id: number;
  name: string;
  serviceId: number;
}

const getLoyaltyPrograms = async (params: Params) => {
  const { data } = await axios.get<LoyaltyProgram[]>(
    `/api/internal/loyalty-programs`,
    {
      params: {
        service: params?.service,
      },
    },
  );
  return data;
};

const updateLoyaltyProgram = async (updateParams: UpdateParams) => {
  const { data } = await axios.put<LoyaltyProgram>(
    `/api/internal/loyalty-programs/${updateParams.id}`,
    {
      name: updateParams.name,
      serviceId: updateParams.serviceId,
    },
  );
  return data;
};

const deleteLoyaltyProgram = async (id: number) => {
  await axios.delete(`/api/internal/loyalty-programs/${id}`);
};

const restoreLoyaltyProgram = async (id: number) => {
  await axios.post(`/api/internal/loyalty-programs/${id}/restore`);
};

const useInternalLoyaltyProgram = ({ immediate = true }: Props = {}) => {
  const {
    data: loyaltyProgram,
    isLoading,
    isError,
    refetch,
  } = useQuery(["loyalty-programs"], () => getLoyaltyPrograms({}), {
    enabled: immediate,
  });

  const updateMutation = useMutation((params: UpdateParams) =>
    updateLoyaltyProgram(params),
  );

  const deleteMutation = useMutation((id: number) => deleteLoyaltyProgram(id));

  const restoreMutation = useMutation((id: number) =>
    restoreLoyaltyProgram(id),
  );

  return {
    loyaltyProgram,
    isError,
    isLoading,
    refetch,

    updateMutation: updateMutation.mutate,
    updateMutationAsync: updateMutation.mutateAsync,
    updateMutationLoading: updateMutation.isLoading,
    updateMutationError: updateMutation.isError,

    deleteMutation: deleteMutation.mutate,
    deleteMutationAsync: deleteMutation.mutateAsync,
    deleteMutationLoading: deleteMutation.isLoading,
    deleteMutationError: deleteMutation.isError,

    restoreMutation: restoreMutation.mutate,
    restoreMutationAsync: restoreMutation.mutateAsync,
    restoreMutationLoading: restoreMutation.isLoading,
    restoreMutationError: restoreMutation.isError,
  };
};

export default useInternalLoyaltyProgram;
